import React from 'react';
import { formatNumber } from 'libphonenumber-js';
import Modal from '@common/components/ConstellationV10Modal';
import { months } from '@common/paymentsUtils/dateUtils';
import { validateAllInputs } from '../validation';
import userPersonas from '@common/constants/userPersonas';
import errorMessages from '@common/constants/errorMessages';
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  Paragraph,
  Select,
  TextButton,
} from '@zillow/constellation-10';
import UnitedStatesSelect from '@common/components/UnitedStatesSelect';
import MaskedInput from '@common/components/ConstellationMaskedInput';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

type Props = {
  businessName;
  dbaName;
  employerIdentificationNumber;
  firstName: string;
  lastName: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  phoneNumber: string;
  verifiedPhoneNumber: string;
  monthOfBirth: string;
  dayOfBirth: string;
  yearOfBirth: string;
  fullSocialSecurityNumber: string;
  editMode: boolean;
  businessStreet;
  businessCity;
  businessState;
  businessZipCode;
  onSubmit: (identityVerificationInfo) => Promise<any>;
  onBack: ({
    businessName,
    dbaName,
    employerIdentificationNumber,
    firstName,
    lastName,
    street,
    city,
    state,
    zipCode,
    phoneNumber,
    monthOfBirth,
    dayOfBirth,
    yearOfBirth,
    fullSocialSecurityNumber,
    businessStreet,
    businessCity,
    businessState,
    businessZipCode,
  }) => void;
  onCancel: () => void;
  onClose: () => void;
  selectedOption: string;
  emergencyMode: boolean;
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
};

const TaxInfoInputPage: React.FC<React.PropsWithChildren<Props>> = (props: Props): JSX.Element => {
  const initialIdVerificationInfo = {
    businessName: {
      value: props.businessName,
      error: '',
    },
    dbaName: {
      value: props.dbaName,
      error: '',
    },
    employerIdentificationNumber: {
      value: props.employerIdentificationNumber,
      error: '',
    },
    firstName: {
      value: props.firstName,
      error: '',
    },

    lastName: {
      value: props.lastName,
      error: '',
    },
    street: {
      value: props.street,
      error: '',
    },
    city: {
      value: props.city,
      error: '',
    },
    state: {
      value: props.state,
      error: '',
    },
    zipCode: {
      value: props.zipCode,
      error: '',
    },
    phoneNumber: {
      value: props.phoneNumber || props.verifiedPhoneNumber,
      error: '',
    },
    monthOfBirth: {
      value: props.monthOfBirth,
      error: '',
    },
    dayOfBirth: {
      value: props.dayOfBirth,
      error: '',
    },
    yearOfBirth: {
      value: props.yearOfBirth,
      error: '',
    },
    fullSocialSecurityNumber: {
      value: props.fullSocialSecurityNumber,
      error: '',
    },
    businessStreet: {
      value: props.businessStreet,
      error: '',
    },
    businessCity: {
      value: props.businessCity,
      error: '',
    },
    businessState: {
      value: props.businessState,
      error: '',
    },
    businessZipCode: {
      value: props.businessZipCode,
      error: '',
    },
  };

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [idVerificationInfo, setIdVerificationInfo] = React.useState<Record<string, any>>(initialIdVerificationInfo);

  const {
    businessName,
    dbaName,
    employerIdentificationNumber,
    firstName,
    lastName,
    street,
    city,
    state,
    zipCode,
    phoneNumber,
    monthOfBirth,
    dayOfBirth,
    yearOfBirth,
    fullSocialSecurityNumber,
    businessStreet,
    businessCity,
    businessState,
    businessZipCode,
  } = idVerificationInfo;

  const onInputChange = (e): void => {
    setIdVerificationInfo({
      ...idVerificationInfo,
      [e.currentTarget.dataset.statekey]: {
        value: e.currentTarget.value,
        error: '',
      },
    });
  };

  const onMaskedFieldInputChange = (e): void => {
    setIdVerificationInfo({
      ...idVerificationInfo,
      [e.currentTarget.dataset.statekey]: {
        value: e.currentTarget.value.replace(/[^\d]/g, ''),
        error: '',
      },
    });
  };

  const handleSubmit = async (): Promise<any> => {
    if (!validateAllInputs({ props, state: idVerificationInfo, setIdVerificationInfo })) {
      return;
    }
    let businessFields = {
      businessName: '',
      dbaName: '',
      employerIdentificationNumber: '',
      businessStreet: '',
      businessCity: '',
      businessState: '',
      businessZipCode: '',
    };
    if (props.selectedOption === 'single_member_llc' || props.selectedOption === 'multi_member_llc') {
      businessFields = {
        ...businessFields,
        businessName: businessName.value.trim(),
        dbaName: dbaName.value.trim(),
        employerIdentificationNumber: employerIdentificationNumber.value.trim(),
      };
      if (props.selectedOption === 'multi_member_llc') {
        businessFields = {
          ...businessFields,
          businessStreet: businessStreet.value.trim(),
          businessCity: businessCity.value.trim(),
          businessState: businessState.value,
          businessZipCode: businessZipCode.value.trim(),
        };
      }
    }

    setIsLoading(true);
    const response = await props.onSubmit({
      identityVerificationInfo: {
        editMode: false,
        firstName: firstName.value.trim(),
        lastName: lastName.value.trim(),
        street: street.value.trim(),
        city: city.value.trim(),
        state: state.value,
        zipCode: zipCode.value.trim(),
        phoneNumber: formatNumber({ country: 'US', phone: phoneNumber.value }, 'NATIONAL'),
        monthOfBirth: monthOfBirth.value,
        dayOfBirth: dayOfBirth.value.trim(),
        yearOfBirth: yearOfBirth.value.trim(),
        fullSocialSecurityNumber: fullSocialSecurityNumber.value.trim(),
        ...businessFields,
        selectedTaxVerificationType: props.selectedOption,
      },
    });

    if (response && response.error) {
      setIsLoading(false);
      setIdVerificationInfo({
        ...idVerificationInfo,
        phoneNumber: response.phoneNumberInvalid
          ? {
              ...phoneNumber,
              error: errorMessages.PHONE_NUMBER_INVALID,
            }
          : { ...phoneNumber },
      });
    } else {
      IreneOnboardingCSEvents.emitSubmitFromTaxVerificationPageEvent({
        currentProperty: props.currentProperty,
        user: props.user,
      });
    }
  };

  const onBack = (): void => {
    props.onBack({
      businessName: businessName.value?.trim(),
      dbaName: dbaName.value?.trim(),
      employerIdentificationNumber: employerIdentificationNumber.value?.trim(),
      firstName: firstName.value?.trim(),
      lastName: lastName.value?.trim(),
      street: street.value?.trim(),
      city: city.value?.trim(),
      state: state.value,
      zipCode: zipCode.value?.trim(),
      phoneNumber: phoneNumber.value?.trim(),
      monthOfBirth: monthOfBirth.value,
      dayOfBirth: dayOfBirth.value?.trim(),
      yearOfBirth: yearOfBirth.value?.trim(),
      fullSocialSecurityNumber: fullSocialSecurityNumber.value?.trim(),
      businessStreet: businessStreet.value?.trim(),
      businessCity: businessCity.value?.trim(),
      businessState: businessState.value,
      businessZipCode: businessZipCode.value?.trim(),
    });
  };

  const onCancel = (): void => {
    props.onCancel();
  };

  const renderMonthOptions = (): Array<JSX.Element> | null =>
    months.map((monthOption, index) => (
      <option key={monthOption} value={(index + 1).toString()}>
        {monthOption}
      </option>
    ));

  const renderPhoneNumberReminder = (): JSX.Element | null => {
    const hasVerifiedPhoneNumber = props.verifiedPhoneNumber;
    const enteredNumberMatchesVerifiedNumber = props.verifiedPhoneNumber === phoneNumber.value;

    if (hasVerifiedPhoneNumber && enteredNumberMatchesVerifiedNumber && !phoneNumber.error) {
      return (
        <Paragraph textStyle="fineprint">
          This is the phone number you used to set up this property. Changing it here will change it for your property.
        </Paragraph>
      );
    }
    return null;
  };

  const renderIndividualTaxForm = () => {
    return (
      <Box
        css={{
          display: 'grid',
          gridTemplateRows: 'auto',
          gap: 'token(spacing.sm) !important',
          padding: 'token(spacing.lg) !important',
        }}
      >
        <Paragraph textStyle="body-lg-bold">Verify your individual tax information</Paragraph>

        <Paragraph textStyle="body-sm">Enter your information as it appears on your tax documents.</Paragraph>
        <FormField
          control={
            <Input
              fluid
              size="md"
              type="text"
              className="fs-exclude"
              data-statekey="firstName"
              value={firstName.value}
              error={firstName.error}
              onChange={onInputChange}
            />
          }
          description={firstName.error && <FormHelp error>{firstName.error}</FormHelp>}
          label={<Label>Legal first name</Label>}
        />
        <FormField
          control={
            <Input
              fluid
              size="md"
              type="text"
              data-statekey="lastName"
              className="fs-exclude"
              value={lastName.value}
              error={lastName.error}
              onChange={onInputChange}
            />
          }
          description={lastName.error && <FormHelp error>{lastName.error}</FormHelp>}
          label={<Label>Legal last name</Label>}
        />
        <FormField
          control={
            <Input
              fluid
              size="md"
              data-statekey="street"
              className="fs-exclude"
              value={street.value}
              error={street.error}
              onChange={onInputChange}
            />
          }
          description={
            <>
              {street.error && <FormHelp error>{street.error}</FormHelp>}
              <Paragraph textStyle="fineprint">Not a post office box or the rental property address.</Paragraph>
            </>
          }
          label={<Label>Current street address</Label>}
        />

        <Box css={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', gap: 'token(spacing.xs)' }}>
          <FormField
            control={
              <Input
                fluid
                size="md"
                data-statekey="city"
                className="fs-exclude"
                value={city.value}
                error={city.error}
                onChange={onInputChange}
              />
            }
            description={city.error && <FormHelp error>{city.error}</FormHelp>}
            label={<Label>City</Label>}
          />
          <FormField
            control={
              <UnitedStatesSelect
                data-statekey="state"
                className="fs-exclude"
                onChange={onInputChange}
                value={state.value}
                error={state.error}
              />
            }
            description={state.error && <FormHelp error>{state.error}</FormHelp>}
            label={<Label>State</Label>}
          />
          <FormField
            control={
              <Input
                fluid
                size="md"
                data-statekey="zipCode"
                className="fs-exclude"
                value={zipCode.value}
                error={zipCode.error}
                onChange={onInputChange}
              />
            }
            description={zipCode.error && <FormHelp error>{zipCode.error}</FormHelp>}
            label={<Label>ZIP code</Label>}
          />
        </Box>

        <FormField
          control={
            <MaskedInput
              statekey="phoneNumber"
              className="fs-exclude"
              type="tel"
              placeholder="Enter phone number"
              value={phoneNumber.value}
              error={phoneNumber.error}
              mask="(999) 999-9999"
              onChange={onMaskedFieldInputChange}
            />
          }
          description={
            <>
              {phoneNumber.error && <FormHelp error>{phoneNumber.error}</FormHelp>}
              {renderPhoneNumberReminder()}
            </>
          }
          label={<Label>Phone number</Label>}
        ></FormField>

        <Box>
          <Label css={{ paddingBottom: 'token(spacing.sm) !important' }}>Date of birth</Label>
          <Box css={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 'token(spacing.xs)' }}>
            <FormField
              control={
                <Select
                  data-statekey="monthOfBirth"
                  className="fs-exclude"
                  value={monthOfBirth.value}
                  error={monthOfBirth.error}
                  onChange={onInputChange}
                >
                  <option value="" />
                  {renderMonthOptions()}
                </Select>
              }
              description={monthOfBirth.error && <FormHelp error>{monthOfBirth.error}</FormHelp>}
            />
            <FormField
              control={
                <Input
                  className="fs-exclude"
                  data-statekey="dayOfBirth"
                  placeholder="DD"
                  value={dayOfBirth.value}
                  error={dayOfBirth.error}
                  onChange={onInputChange}
                />
              }
              description={dayOfBirth.error && <FormHelp error>{dayOfBirth.error}</FormHelp>}
            />
            <FormField
              control={
                <MaskedInput
                  className="fs-exclude"
                  statekey="yearOfBirth"
                  placeholder="YYYY"
                  value={yearOfBirth.value}
                  error={yearOfBirth.error}
                  onChange={onMaskedFieldInputChange}
                  mask="9999"
                />
              }
              description={yearOfBirth.error && <FormHelp error>{yearOfBirth.error}</FormHelp>}
            />
          </Box>
        </Box>
        <FormField
          control={
            <MaskedInput
              className="fs-exclude"
              statekey="fullSocialSecurityNumber"
              mask="999-99-9999"
              placeholder="XXX&#8209;XX&#8209;XXXX"
              value={fullSocialSecurityNumber.value}
              error={fullSocialSecurityNumber.error}
              onChange={onMaskedFieldInputChange}
            />
          }
          description={
            <>
              {fullSocialSecurityNumber.error && <FormHelp error>{fullSocialSecurityNumber.error}</FormHelp>}
              <Paragraph textStyle="fineprint">Your full Social Security number is needed for tax purposes.</Paragraph>
            </>
          }
          label={<Label>Social Security number</Label>}
        ></FormField>
      </Box>
    );
  };

  const renderBusinessTaxForm = () => {
    return (
      <Box
        css={{
          display: 'grid',
          gridTemplateRows: 'auto',
          gap: 'token(spacing.sm) !important',
          padding: 'token(spacing.lg) !important',
        }}
      >
        <Paragraph textStyle="body-lg-bold">
          {props.selectedOption === 'single_member_llc'
            ? 'Verify your single LLC tax information'
            : 'Verify your multi-member LLC or corporation tax information'}
        </Paragraph>

        <Paragraph textStyle="body-sm">Enter the business&apos;s information as it appears on tax documents.</Paragraph>

        <FormField
          control={
            <Input
              data-statekey="businessName"
              className="fs-exclude"
              placeholder="Enter the business name"
              value={businessName.value}
              error={businessName.error}
              onChange={onInputChange}
            />
          }
          description={businessName.error && <FormHelp error>{businessName.error}</FormHelp>}
          label={<Label>Business name</Label>}
        />

        <FormField
          control={
            <Input
              data-statekey="dbaName"
              className="fs-exclude"
              placeholder="Enter DBA name"
              value={dbaName.value}
              error={dbaName.error}
              onChange={onInputChange}
            />
          }
          description={
            <>
              {dbaName.error && <FormHelp error>{dbaName.error}</FormHelp>}
              <Paragraph textStyle="fineprint">
                Fill in only if the business operates under a name other than the registered name.
              </Paragraph>
            </>
          }
          label={
            <Label>
              Doing Business As (DBA) name&nbsp;
              <i>(if applicable)</i>
            </Label>
          }
        />
        <FormField
          control={
            <MaskedInput
              statekey="employerIdentificationNumber"
              className="fs-exclude"
              mask="99-9999999"
              placeholder="XX-XXXXXXX"
              value={employerIdentificationNumber.value}
              error={employerIdentificationNumber.error}
              onChange={onMaskedFieldInputChange}
            />
          }
          description={
            <>
              {employerIdentificationNumber.error && <FormHelp error>{employerIdentificationNumber.error}</FormHelp>}
              <Paragraph textStyle="fineprint">
                Check your tax documents or business loan applications, or call the IRS.
              </Paragraph>
            </>
          }
          label={<Label>Employer Identification Number (EIN)</Label>}
        />

        {props.selectedOption === 'multi_member_llc' && (
          <>
            <FormField
              control={
                <Input
                  data-statekey="businessStreet"
                  className="fs-exclude"
                  placeholder="Enter street address"
                  value={businessStreet.value}
                  error={businessStreet.error}
                  onChange={onInputChange}
                />
              }
              description={
                <>
                  {businessStreet.error && <FormHelp error>{businessStreet.error}</FormHelp>}
                  <Paragraph textStyle="fineprint">Not a post office box or the rental property address.</Paragraph>
                </>
              }
              label={<Label>Business street address</Label>}
            />

            <Box css={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 'token(spacing.xs)' }}>
              <FormField
                control={
                  <Input
                    data-statekey="businessCity"
                    className="fs-exclude"
                    placeholder="Enter city"
                    value={businessCity.value}
                    error={businessCity.error}
                    onChange={onInputChange}
                  />
                }
                description={businessCity.error && <FormHelp error>{businessCity.error}</FormHelp>}
                label={<Label>City</Label>}
              />

              <FormField
                control={
                  <UnitedStatesSelect
                    data-statekey="businessState"
                    className="fs-exclude prs"
                    onChange={onInputChange}
                    value={businessState.value}
                    error={businessState.error}
                  />
                }
                description={businessState.error && <FormHelp error>{businessState.error}</FormHelp>}
                label={<Label>State</Label>}
              />
              <FormField
                control={
                  <Input
                    data-statekey="businessZipCode"
                    className="fs-exclude"
                    placeholder="Zip"
                    value={businessZipCode.value}
                    error={businessZipCode.error}
                    onChange={onInputChange}
                  />
                }
                description={businessZipCode.error && <FormHelp error>{businessZipCode.error}</FormHelp>}
                label={<Label>ZIP code</Label>}
              />
            </Box>
            <Divider />
            <Paragraph textStyle="body-lg-bold">Owner information</Paragraph>
          </>
        )}

        <FormField
          control={
            <Input
              data-statekey="firstName"
              className="fs-exclude"
              placeholder="Enter Owner first name"
              value={firstName.value}
              error={firstName.error}
              onChange={onInputChange}
            />
          }
          description={firstName.error && <FormHelp error>{firstName.error}</FormHelp>}
          label={<Label>Owner first name</Label>}
        />

        <FormField
          control={
            <Input
              data-statekey="lastName"
              className="fs-exclude"
              placeholder="Enter Owner last name"
              value={lastName.value}
              error={lastName.error}
              onChange={onInputChange}
            />
          }
          description={lastName.error && <FormHelp error>{lastName.error}</FormHelp>}
          label={<Label>Owner last name</Label>}
        />

        <FormField
          control={
            <Input
              data-statekey="street"
              className="fs-exclude"
              placeholder="Enter street address"
              value={street.value}
              error={street.error}
              onChange={onInputChange}
            />
          }
          description={
            <>
              {street.error && <FormHelp error>{street.error}</FormHelp>}
              <Paragraph textStyle="fineprint">Not a post office box or the rental property address.</Paragraph>
            </>
          }
          label={<Label>Owner street address</Label>}
        />

        <Box css={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 'token(spacing.xs)' }}>
          <FormField
            control={
              <Input
                data-statekey="city"
                className="fs-exclude"
                placeholder="Enter city"
                value={city.value}
                error={city.error}
                onChange={onInputChange}
              />
            }
            description={city.error && <FormHelp error>{city.error}</FormHelp>}
            label={<Label>City</Label>}
          />

          <FormField
            control={
              <UnitedStatesSelect
                data-statekey="state"
                className="fs-exclude"
                onChange={onInputChange}
                value={state.value}
                error={state.error}
              />
            }
            description={state.error && <FormHelp error>{state.error}</FormHelp>}
            label={<Label>State</Label>}
          />

          <FormField
            control={
              <Input
                data-statekey="zipCode"
                className="fs-exclude"
                placeholder="Zip"
                value={zipCode.value}
                error={zipCode.error}
                onChange={onInputChange}
              />
            }
            description={zipCode.error && <FormHelp error>{zipCode.error}</FormHelp>}
            label={<Label>Owner ZIP code</Label>}
          />
        </Box>

        <FormField
          control={
            <MaskedInput
              statekey="phoneNumber"
              className="fs-exclude"
              type="tel"
              placeholder="Enter phone number"
              value={phoneNumber.value}
              error={phoneNumber.error}
              mask="(999) 999-9999"
              onChange={onMaskedFieldInputChange}
            />
          }
          description={
            <>
              {phoneNumber.error && <FormHelp error>{phoneNumber.error}</FormHelp>}
              {renderPhoneNumberReminder()}
            </>
          }
          label={<Label>Phone number</Label>}
        />

        <Box>
          <Label>Date of birth</Label>
        </Box>
        <Box css={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 'token(spacing.xs)' }}>
          <FormField
            control={
              <Select
                data-statekey="monthOfBirth"
                className="fs-exclude"
                value={monthOfBirth.value}
                error={monthOfBirth.error}
                onChange={onInputChange}
              >
                <option value="" />
                {renderMonthOptions()}
              </Select>
            }
            description={monthOfBirth.error && <FormHelp error>{monthOfBirth.error}</FormHelp>}
          />

          <FormField
            control={
              <Input
                className="fs-exclude"
                data-statekey="dayOfBirth"
                placeholder="DD"
                value={dayOfBirth.value}
                error={dayOfBirth.error}
                onChange={onInputChange}
              />
            }
            description={dayOfBirth.error && <FormHelp error>{dayOfBirth.error}</FormHelp>}
          />

          <FormField
            control={
              <MaskedInput
                className="fs-exclude"
                statekey="yearOfBirth"
                placeholder="yyyy"
                value={yearOfBirth.value}
                error={yearOfBirth.error}
                onChange={onMaskedFieldInputChange}
                mask="9999"
              />
            }
            description={yearOfBirth.error && <FormHelp error>{yearOfBirth.error}</FormHelp>}
          />
        </Box>

        <FormField
          control={
            <MaskedInput
              className="fs-exclude"
              statekey="fullSocialSecurityNumber"
              mask="999-99-9999"
              placeholder="XXX&#8209;XX&#8209;XXXX"
              value={fullSocialSecurityNumber.value}
              error={fullSocialSecurityNumber.error}
              onChange={onMaskedFieldInputChange}
            />
          }
          description={
            <>
              {fullSocialSecurityNumber.error && <FormHelp error>{fullSocialSecurityNumber.error}</FormHelp>}
              <Paragraph textStyle="fineprint">Your full Social Security number is needed for tax purposes.</Paragraph>
            </>
          }
          label={<Label>Social Security number</Label>}
        />
      </Box>
    );
  };

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      onRequestClose={props.emergencyMode ? undefined : props.onClose}
      header={
        <Box css={{ padding: 'token(spacing.sm) !important' }}>
          <TextButton textStyle="body-sm-bold" tone="brand" css={{ float: 'left' }} onClick={onBack}>
            Back
          </TextButton>
          <Heading level={3} textStyle="heading-xs" css={{ display: 'flex', justifyContent: 'center' }}>
            Payment setup
          </Heading>
        </Box>
      }
      body={<Box>{props.selectedOption === 'individual' ? renderIndividualTaxForm() : renderBusinessTaxForm()}</Box>}
      footer={
        <ButtonGroup aria-label="footer" css={{ padding: 'token(spacing.sm) !important' }}>
          <Button tone="brand" emphasis="filled" onClick={handleSubmit} loading={isLoading}>
            {props.editMode ? 'Update' : 'Continue'}
          </Button>
          {props.editMode && (
            <Button onClick={onCancel} tone="brand" emphasis="outlined">
              Cancel
            </Button>
          )}
        </ButtonGroup>
      }
    />
  );
};

TaxInfoInputPage.defaultProps = {
  businessName: '',
  dbaName: '',
  employerIdentificationNumber: '',
  firstName: '',
  lastName: '',
  street: '',
  city: '',
  state: '',
  zipCode: '',
  phoneNumber: '',
  verifiedPhoneNumber: '',
  monthOfBirth: '',
  dayOfBirth: '',
  yearOfBirth: '',
  fullSocialSecurityNumber: '',
  editMode: false,
  businessStreet: '',
  businessCity: '',
  businessState: '',
  businessZipCode: '',
};

export default TaxInfoInputPage;
